import { useState } from "react";
import useSWR from "swr";

import { api, ApiError, ExportFilters, UserMe, WatchlistSummary } from "../api";
import { PageHeader } from "../ui";

export function Export({ user }: { user: UserMe }) {
  return (
    <section>
      <PageHeader
        title="Data export"
        description="Download fund metadata and return series as CSV, scoped to your plan."
      />
      <DataExport canBulk={user.tier.can_bulk_export} tierName={user.tier.display_name} permissions={user.permissions} />
    </section>
  );
}

const selectClass = "px-3 py-1.5 rounded-md text-xs font-sans outline-none bg-input border border-input-border text-main focus:border-accent";

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[] | undefined;
  onChange: (v: string) => void;
}) {
  if (!options || options.length === 0) return null;
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={selectClass}>
      <option value="">All {label}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

function DataExport({ canBulk, tierName, permissions }: { canBulk: boolean; tierName: string; permissions: string[] }) {
  const canExportReturns = permissions.includes("nh.returns.export") || permissions.includes("nh.admin");
  const { data: watchlists } = useSWR<WatchlistSummary[]>("/watchlists", (p: string) => api.get<WatchlistSummary[]>(p));
  const { data: filters } = useSWR<ExportFilters>("/metadata/filters", (p: string) => api.get<ExportFilters>(p));
  const [selectedWl, setSelectedWl] = useState("");
  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [strategy, setStrategy] = useState("");
  const [style, setStyle] = useState("");
  const [sector, setSector] = useState("");
  const [metaLoading, setMetaLoading] = useState(false);
  const [tsLoading, setTsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const buildParams = (extra: Record<string, string> = {}) => {
    const p = new URLSearchParams();
    if (selectedWl) p.set("watchlist_id", selectedWl);
    for (const [k, v] of Object.entries(extra)) {
      if (v) p.set(k, v);
    }
    const s = p.toString();
    return s ? `?${s}` : "";
  };

  const params = buildParams({ country, region, strategy, style, sector });

  const hasFilters = !!(selectedWl || country || region || strategy || style || sector);

  const download = async (path: string, filename: string, setLoading: (v: boolean) => void) => {
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const next = await api.download(path, filename);
      if (next !== null) {
        setNotice(`Export truncated at your plan's row limit. Narrow the filters, or continue via the API with offset=${next}.`);
      }
    } catch (e) {
      setError(e instanceof ApiError ? `Export failed (${e.status}): ${e.message}` : "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSelectedWl("");
    setCountry("");
    setRegion("");
    setStrategy("");
    setStyle("");
    setSector("");
  };

  return (
    <div className="flex flex-col gap-4 p-6 rounded-lg bg-card border border-border">
      {canBulk ? (
        <>
          <p className="text-sm text-muted">
            Exports cover what your {tierName} plan can access. Use the filters to narrow them.
          </p>

          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            {watchlists && watchlists.length > 0 && (
              <select value={selectedWl} onChange={(e) => setSelectedWl(e.target.value)} className={selectClass}>
                <option value="">All funds</option>
                {watchlists.map((wl) => (
                  <option key={wl.id} value={wl.id}>{wl.name} ({wl.item_count})</option>
                ))}
              </select>
            )}
            <FilterSelect label="countries" value={country} options={filters?.country} onChange={setCountry} />
            <FilterSelect label="regions" value={region} options={filters?.region} onChange={setRegion} />
            <FilterSelect label="strategies" value={strategy} options={filters?.strategy} onChange={setStrategy} />
            <FilterSelect label="styles" value={style} options={filters?.style} onChange={setStyle} />
            <FilterSelect label="sectors" value={sector} options={filters?.sector} onChange={setSector} />
            {hasFilters && (
              <button className="btn-sm btn-default" onClick={clearFilters}>Clear</button>
            )}
          </div>

          {/* Export buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              className="btn-default btn-sm"
              disabled={metaLoading}
              onClick={() => download(`/metadata/export.csv${params}`, "nh_metadata.csv", setMetaLoading)}
            >
              {metaLoading ? "Exporting..." : "Metadata CSV"}
            </button>
            {canExportReturns ? (
              <button
                className="btn-default btn-sm"
                disabled={tsLoading}
                onClick={() => download(`/market-data/timeseries/export.csv${params}`, "nh_timeseries.csv", setTsLoading)}
              >
                {tsLoading ? "Exporting..." : "Time series CSV"}
              </button>
            ) : (
              <span className="inline-flex items-center text-[11px] text-muted px-2.5 py-1">
                Time series export requires nh.returns.export permission
              </span>
            )}
          </div>
          {error && <p className="text-xs text-neg">{error}</p>}
          {notice && <p className="text-xs text-muted">{notice}</p>}
        </>
      ) : (
        <p className="text-sm text-muted">
          CSV export isn't on the Free plan. <a href="/pricing">Compare plans</a>
        </p>
      )}
    </div>
  );
}
